import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import { CompanyDTO, Nullable } from "@shaarp/common";
import { CheckCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { useToastError } from "../ui/use-toast";
import AvatarCompany from "./avatar-company";

export default function CompanySelector({
  company,
  onChange,
  disabled = false,
  className = "",
}: {
  company: Nullable<CompanyDTO>;
  onChange: (newCompany: Nullable<CompanyDTO>) => void;
  disabled?: boolean;
  className?: string;
}) {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [companiesOptions, setCompaniesOptions] = useState<CompanyDTO[]>([]);
  const [companiesLastViewed, setCompaniesLastViewed] = useState<CompanyDTO[]>(
    []
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [inputSearch, setInputSearch] = useState<string>("");

  useEffect(() => {
    const abortController = new AbortController();
    fetchCompaniesLastViewed(abortController.signal);
    return () => abortController.abort();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleInputSearch();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [inputSearch]);

  const fetchCompaniesLastViewed = async (signal?: AbortSignal) => {
    try {
      const { data } = await api.get<CompanyDTO[]>(
        `/my/user/history/companies`,
        {
          params: { limit: 5 },
          signal,
        },
      );
      setCompaniesLastViewed(data);
    } catch {
      // Silently ignore aborted requests and transient errors
    }
  };

  const handleInputSearch = async () => {
    // get company id from inputSearch with uuid format
    const uuid = inputSearch.match(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/
    );
    if (uuid && uuid[0]) {
      await getCompanyOptionById(uuid[0]);
      return;
    }

    if (inputSearch.length > 0) {
      await searchAvailable();
    } else {
      setCompaniesOptions([]);
    }
  };

  const getCompanyOptionById = async (companyId: string) => {
    const { data } = await api.get<CompanyDTO>(
      `/company/available/${companyId}`
    );
    setCompaniesOptions([data]);
  };

  const searchAvailable = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get<CompanyDTO[]>(`/companies/available`, {
        params: {
          search: inputSearch,
          limit: 10,
        },
      });
      setCompaniesOptions(data);
    } catch (e) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const select = async (newCompany: CompanyDTO | null) => {
    setOpen(false);
    onChange(newCompany);
  };

  return (
    <div className={cn("relative w-[400px]", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={t("company.selector.selectTeam")}
            className={cn("gap-3 justify-between p-4 w-full")}
            disabled={disabled}
          >
            <AvatarCompany
              className="w-6 h-6 cursor-pointer"
              company={company}
            />
            {company?.name || t("company.selector.notAssigned")}
            <CaretSortIcon className="ml-auto w-4 h-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-full" side="bottom" align="start">
          <Command>
            <CommandInput
              placeholder={t("company.selector.search")}
              onValueChange={setInputSearch}
            />
            <CommandList>
              {isLoading && (
                <CommandGroup
                  forceMount={true}
                  heading={t("company.selector.loading")}
                >
                  <CommandItem disabled={true} forceMount={true}>
                    {t("company.selector.loading")}
                  </CommandItem>
                </CommandGroup>
              )}
              {!isLoading && company && (
                <>
                  <CommandGroup forceMount={true}>
                    <CommandItem
                      key={"Non assigné"}
                      value={"Non assigné"}
                      onSelect={() => select(null)}
                      forceMount={true}
                    >
                      <Avatar className="mr-3 w-6 h-6">
                        <AvatarFallback>NA</AvatarFallback>
                      </Avatar>
                      {t("company.selector.notAssigned")}
                      <CheckIcon
                        className={cn(
                          "ml-auto w-4 h-4",
                          !company ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}
              {!isLoading &&
                inputSearch.length > 0 &&
                companiesOptions.length === 0 && (
                  <CommandGroup
                    forceMount={true}
                    heading={t("company.selector.available")}
                  >
                    <CommandItem disabled={true} forceMount={true}>
                      {t("company.selector.noCompanyFound")}
                    </CommandItem>
                  </CommandGroup>
                )}
              {!isLoading && companiesOptions.length > 0 && (
                <CommandGroup
                  forceMount={true}
                  heading={t("company.selector.available")}
                >
                  {companiesOptions
                    .filter((companyOption) => companyOption.id !== company?.id)
                    .map((companyOption) => (
                      <CompanyItem
                        key={companyOption.id}
                        company={companyOption}
                        selected={companyOption.id === company?.id}
                        onSelect={() => select(companyOption)}
                      />
                    ))}
                </CommandGroup>
              )}
              {!isLoading &&
                inputSearch.length === 0 &&
                companiesOptions.length === 0 &&
                companiesLastViewed.length > 0 && (
                  <CommandGroup
                    forceMount={true}
                    heading={t("company.selector.lastViewed")}
                  >
                    {companiesLastViewed.map((companyOption) => (
                      <CompanyItem
                        key={companyOption.id}
                        company={companyOption}
                        selected={companyOption.id === company?.id}
                        onSelect={() => select(companyOption)}
                      />
                    ))}
                  </CommandGroup>
                )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

const CompanyItem = ({
  company,
  selected,
  onSelect,
}: {
  company: CompanyDTO;
  selected: boolean;
  onSelect: () => void;
}) => {
  return (
    <CommandItem
      key={company.id}
      value={company.name}
      disabled={false}
      onSelect={onSelect}
    >
      <AvatarCompany className="mr-3 w-6 h-6" company={company} />
      {company.name}
      {company.isVerified && <CheckCircleIcon className="ml-auto w-4 h-4" />}
      <CheckIcon
        className={cn(
          "ml-auto w-4 h-4",
          selected ? "opacity-100" : "opacity-0"
        )}
      />
    </CommandItem>
  );
};
